import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Calendar, Clock, User, Phone, Mail, Navigation, MapPin, Loader2, FileText, Camera,
  Wrench, KeyRound, Users, ExternalLink, Zap, Image as ImageIcon,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CAT_LABELS = {
  panel: 'Solar panels', inverter: 'Inverter', battery: 'Battery', ev_charger: 'EV charger',
  generation_meter: 'Generation meter', consumer_unit: 'Consumer unit',
};
const SITE_LABELS = {
  distribution_board_location: 'Distribution board', distance_from_inverter: 'DB → inverter distance',
  additional_db_required: 'Additional DB required', inverter_battery_location: 'Inverter / battery location',
  cable_distance: 'Cable distance', cable_route: 'Cable route', roof_tile_type: 'Roof tile',
  hook_stops_required: 'Hook stops required', num_roofs: 'No. of roofs', roof_condition: 'Roof condition',
  roof_obstructions: 'Roof obstructions', shading_description: 'Shading', notes: 'Surveyor notes',
};
const GROUP_ORDER = [['technical', 'Technical'], ['safety', 'Safety'], ['customer', 'Customer paperwork']];

const openAuthed = async (url, filename) => {
  try {
    const res = await api.get(url, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(res.data);
    const w = window.open(blobUrl, '_blank');
    if (!w) {
      const a = document.createElement('a'); a.href = blobUrl; a.download = filename || 'document.pdf'; a.click();
    }
  } catch {
    toast.error('Could not open the document');
  }
};

const Section = ({ icon: Icon, title, children, testId, right }) => (
  <Card className="border-slate-200 dark:border-slate-700" data-testid={testId}>
    <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
      <CardTitle className="text-base flex items-center gap-2"><Icon className="w-4 h-4 text-blue-500" />{title}</CardTitle>
      {right}
    </CardHeader>
    <CardContent className="space-y-3">{children}</CardContent>
  </Card>
);

/** Engineer job detail: contact, site/access notes, equipment, documents, photos. */
export const EngineerJobDetail = ({ job, onUploadPhoto, refreshKey }) => {
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!job?.customer_id) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/engineer/job/${job.customer_id}/pack`)
      .then(({ data }) => { if (!cancelled) setPack(data); })
      .catch(() => toast.error('Failed to load job details'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [job?.customer_id, refreshKey]);

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!pack) return <p className="text-center text-slate-400 py-8">Job details unavailable</p>;

  const c = pack.customer;
  const fullAddress = [c.address, c.city, c.postcode].filter(Boolean).join(', ');
  const openMaps = () => window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`, '_blank');
  const photos = pack.documents.filter((d) => d.group === 'photos');
  const grouped = GROUP_ORDER.map(([g, label]) => [label, pack.documents.filter((d) => d.group === g)]).filter(([, ds]) => ds.length);
  const sys = pack.system || {};
  const latestSurvey = pack.surveys?.[0];
  const siteFacts = latestSurvey ? Object.entries(latestSurvey.site).filter(([, v]) => v && String(v).trim()) : [];
  const accessNotes = [pack.installation?.description, pack.notes].filter((t) => t && t.trim());

  return (
    <div className="space-y-4" data-testid="engineer-job-pack">
      {/* Job header */}
      <Card className="border-slate-200 dark:border-slate-700">
        <CardContent className="pt-5 space-y-3">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white" data-testid="eng-job-title">{job.title}</h2>
          <div className="flex flex-wrap gap-2">
            <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"><Calendar className="w-3 h-3 mr-1" />{formatDate(job.scheduled_date)}{job.end_date && job.end_date !== job.scheduled_date ? ` → ${formatDate(job.end_date)}` : ''}</Badge>
            <Badge variant="outline"><Clock className="w-3 h-3 mr-1" />{job.start_time} – {job.end_time}</Badge>
            {sys.installation_days > 1 && <Badge variant="outline">{sys.installation_days} days</Badge>}
          </div>
          {pack.crew?.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400" data-testid="eng-job-crew">
              <Users className="w-3.5 h-3.5" /> Team:
              {pack.crew.map((e) => (
                <span key={e.id} className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5">
                  <span className="w-2 h-2 rounded-full" style={{ background: e.color || '#3b82f6' }} />{e.name}
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customer + contact */}
      <Section icon={User} title="Customer" testId="eng-customer-card">
        <p className="font-semibold text-slate-900 dark:text-white">{c.first_name} {c.last_name}{c.company_name ? <span className="text-slate-500 font-normal"> · {c.company_name}</span> : null}</p>
        <div className="grid grid-cols-3 gap-2">
          <Button variant="outline" size="sm" className="flex-col h-auto py-3 text-xs" onClick={() => { window.location.href = `tel:${c.phone}`; }} data-testid="eng-call-btn"><Phone className="w-5 h-5 mb-1 text-emerald-600" />Call</Button>
          <Button variant="outline" size="sm" className="flex-col h-auto py-3 text-xs" onClick={() => { window.location.href = `mailto:${c.email}`; }} data-testid="eng-email-btn"><Mail className="w-5 h-5 mb-1 text-blue-600" />Email</Button>
          <Button variant="outline" size="sm" className="flex-col h-auto py-3 text-xs" onClick={openMaps} data-testid="eng-navigate-btn"><Navigation className="w-5 h-5 mb-1 text-purple-600" />Navigate</Button>
        </div>
        <div className="flex items-start gap-2 text-sm">
          <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-slate-700 dark:text-slate-300">{c.address}{c.city ? `, ${c.city}` : ''}</p>
            {c.postcode && <p className="text-slate-500 font-medium">{c.postcode}</p>}
            {c.mpan && <p className="text-xs text-slate-500 mt-1">MPAN {c.mpan}</p>}
          </div>
        </div>
      </Section>

      {/* Site & access notes */}
      <Section icon={KeyRound} title="Site & access notes" testId="eng-access-card">
        {accessNotes.length === 0 && siteFacts.length === 0 ? (
          <p className="text-sm text-slate-400">No site notes recorded.</p>
        ) : (
          <>
            {accessNotes.map((t, i) => <p key={i} className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{t}</p>)}
            {siteFacts.length > 0 && (
              <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs pt-2 border-t border-slate-200 dark:border-slate-700" data-testid="eng-site-facts">
                {siteFacts.map(([k, v]) => (
                  <div key={k} className={k === 'notes' || k === 'roof_obstructions' || k === 'shading_description' ? 'col-span-2' : ''}>
                    <dt className="text-slate-400">{SITE_LABELS[k] || k}</dt>
                    <dd className="font-medium text-slate-700 dark:text-slate-300">{String(v)}</dd>
                  </div>
                ))}
              </dl>
            )}
            {latestSurvey && <p className="text-[11px] text-slate-400">From site survey {latestSurvey.survey_date ? formatDate(latestSurvey.survey_date) : ''}{latestSurvey.surveyor_name ? ` by ${latestSurvey.surveyor_name}` : ''}</p>}
          </>
        )}
      </Section>

      {/* Equipment */}
      <Section icon={Wrench} title="Equipment to install" testId="eng-equipment-card">
        <div className="flex flex-wrap gap-1.5">
          {sys.system_size > 0 && <Badge variant="outline"><Zap className="w-3 h-3 mr-1" />{sys.system_size} kW</Badge>}
          {sys.no_of_phases && <Badge variant="outline">{sys.no_of_phases} phase</Badge>}
          {sys.num_strings && sys.panels_per_string && <Badge variant="outline">{sys.num_strings} × {sys.panels_per_string} string layout</Badge>}
          {sys.types?.map((t) => <Badge key={t} className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">{t.replace(/_/g, ' + ')}</Badge>)}
        </div>
        {pack.equipment.length === 0 ? (
          <p className="text-sm text-slate-400">No equipment recorded yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800 text-sm" data-testid="eng-equipment-list">
            {pack.equipment.map((e, i) => (
              <li key={i} className="flex items-center justify-between py-2 gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-wide text-slate-400">{CAT_LABELS[e.category] || e.category}</p>
                  <p className="font-medium text-slate-800 dark:text-slate-200 truncate">{[e.manufacturer, e.model].filter(Boolean).join(' ') || '—'}</p>
                </div>
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex-shrink-0">× {e.qty}</span>
              </li>
            ))}
          </ul>
        )}
        {(pack.additional_extras || sys.electrical_description || sys.other_description) && (
          <div className="pt-2 border-t border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-400 space-y-1">
            {pack.additional_extras && <p><span className="text-slate-400">Extras:</span> {pack.additional_extras}</p>}
            {sys.electrical_description && <p><span className="text-slate-400">Electrical:</span> {sys.electrical_description}</p>}
            {sys.other_description && <p><span className="text-slate-400">Other:</span> {sys.other_description}</p>}
          </div>
        )}
      </Section>

      {/* Documents */}
      <Section icon={FileText} title="Documents" testId="eng-documents-card">
        {pack.surveys?.length > 0 && (
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Survey</p>
            <div className="flex flex-wrap gap-2">
              {pack.surveys.map((s) => (
                <button key={s.id} type="button" onClick={() => openAuthed(s.pdf_url, `survey-${s.survey_date}.pdf`)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2.5 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50"
                  data-testid={`eng-doc-survey-${s.id}`}>
                  <FileText className="w-3.5 h-3.5 text-emerald-600" /> Site survey {s.survey_date ? formatDate(s.survey_date) : ''} <ExternalLink className="w-3 h-3 text-slate-400" />
                </button>
              ))}
            </div>
          </div>
        )}
        {grouped.map(([label, ds]) => (
          <div key={label}>
            <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">{label}</p>
            <div className="flex flex-wrap gap-2">
              {ds.map((d) => (
                <a key={d.id} href={`${API_URL}${d.url}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2.5 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50"
                  data-testid={`eng-doc-${d.id}`}>
                  <FileText className="w-3.5 h-3.5 text-blue-600" /> {d.label} <ExternalLink className="w-3 h-3 text-slate-400" />
                </a>
              ))}
            </div>
          </div>
        ))}
        {!pack.surveys?.length && grouped.length === 0 && <p className="text-sm text-slate-400">No documents on this job yet.</p>}
      </Section>

      {/* Photos */}
      <Section icon={ImageIcon} title={`Installation photos (${photos.length})`} testId="eng-photos-card"
        right={<Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={onUploadPhoto} data-testid="eng-upload-photo-btn"><Camera className="w-4 h-4 mr-1" />Add photo</Button>}>
        {photos.length === 0 ? (
          <p className="text-sm text-slate-400">No photos yet — add before/after shots as you go.</p>
        ) : (
          <div className="grid grid-cols-3 gap-2" data-testid="eng-photo-grid">
            {photos.map((p) => (
              <a key={p.id} href={`${API_URL}${p.url}`} target="_blank" rel="noopener noreferrer" className="block aspect-square overflow-hidden rounded-md bg-slate-100 dark:bg-slate-800">
                <img src={`${API_URL}${p.url}`} alt={p.notes || p.filename} className="w-full h-full object-cover" loading="lazy" />
              </a>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
};

export default EngineerJobDetail;
